import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { GripVertical, Scissors, Trash2, RotateCcw, FileText, GitBranch, Pencil } from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-50 border-blue-200 text-blue-700',
  in_progress: 'bg-amber-50 border-amber-200 text-amber-700',
  completed: 'bg-green-50 border-green-200 text-green-700',
  rolled_over: 'bg-gray-50 border-gray-200 text-gray-500',
};

const STATUS_LABELS = {
  scheduled: '待开始',
  in_progress: '进行中',
  completed: '已完成',
  rolled_over: '已滚动',
};

const ROLLED_NEXT_DAY_CLASS = 'bg-purple-50 border-purple-200 text-purple-700';

function ScheduleTaskCard({ schedule, isOverlay = false, onDelete, onSplit, onExportProcess, onEditNote }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `board-task-${schedule._id}`,
    data: {
      type: 'board-task',
      scheduleId: schedule._id,
      schedule,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRolledNextDay = !!schedule.rolled_from_schedule_id;
  const isSplitChild = !!schedule.parent_schedule_id;
  const isCompleted = schedule.status === 'completed';

  const statusClass = isRolledNextDay
    ? ROLLED_NEXT_DAY_CLASS
    : (STATUS_COLORS[schedule.status] || STATUS_COLORS.scheduled);

  const progress = schedule.assigned_quantity > 0
    ? Math.round((schedule.completed_quantity / schedule.assigned_quantity) * 100)
    : 0;

  const tooltipText = `${schedule.step_name}\n${schedule.production_order_number} · ${schedule.finished_product_code}\n${schedule.finished_product_name || ''}${schedule.specification ? '\n规格: ' + schedule.specification : ''}\n进度: ${schedule.completed_quantity}/${schedule.assigned_quantity} 件 (${progress}%)\n状态: ${isRolledNextDay ? '滚动' : (STATUS_LABELS[schedule.status] || schedule.status)}${schedule.notes ? '\n备注: ' + schedule.notes : ''}`;

  const cardContent = (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        p-2.5 mb-2 border rounded-lg cursor-grab active:cursor-grabbing text-xs
        ${statusClass}
        ${isDragging || isOverlay ? 'shadow-lg ring-2 ring-blue-400 opacity-90 z-50' : ''}
      `}
    >
      {/* Row 1: Order number + Step name + Status badge */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <GripVertical className="h-3.5 w-3.5 opacity-40 shrink-0" />
        <span className="text-[10px] text-blue-700 font-semibold shrink-0 bg-blue-100/50 px-1 py-0.5 rounded" title={`${schedule.production_order_number}-${String(schedule.item_index + 1).padStart(3, '0')}`}>
          {schedule.production_order_number}-{String(schedule.item_index + 1).padStart(3, '0')}
        </span>
        <span className="font-semibold truncate flex-1 text-[13px] leading-tight" title={schedule.step_name}>
          {schedule.step_name}
        </span>
        {isRolledNextDay ? (
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 shrink-0 bg-purple-100 text-purple-700 border-purple-300">
            <RotateCcw className="h-3 w-3 mr-0.5" />
            滚动
          </Badge>
        ) : isSplitChild ? (
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 shrink-0 bg-cyan-100 text-cyan-700 border-cyan-300">
            <GitBranch className="h-3 w-3 mr-0.5" />
            拆分
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 shrink-0">
            {STATUS_LABELS[schedule.status] || schedule.status}
          </Badge>
        )}
      </div>

      {/* Row 2: Product code + name */}
      <div className="text-[11px] truncate mb-0.5" title={`${schedule.finished_product_code}${schedule.finished_product_name ? ' · ' + schedule.finished_product_name : ''}`}>
        <span className="font-medium">{schedule.finished_product_code}</span>
        {schedule.finished_product_name && (
          <span className="ml-1 opacity-80">{schedule.finished_product_name}</span>
        )}
      </div>

      {/* Row 3: Specification (if exists) */}
      {schedule.specification && (
        <div className="text-[11px] text-amber-700 font-medium truncate mb-1" title={schedule.specification}>
          规格: {schedule.specification}
        </div>
      )}

      {/* Row 4: Quantity + actions */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-[11px] shrink-0 opacity-70">
          {schedule.completed_quantity}/{schedule.assigned_quantity}件
        </span>
        {!isOverlay && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onEditNote?.(schedule); }}
              className="p-1 hover:bg-black/5 rounded"
              title="编辑备注/数量"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onExportProcess?.(schedule); }}
              className="p-1 hover:bg-black/5 rounded"
              title="导出工序文件"
            >
              <FileText className="h-3.5 w-3.5" />
            </button>
            {!isCompleted && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onSplit?.(schedule); }}
                  className="p-1 hover:bg-black/5 rounded"
                  title="拆分"
                >
                  <Scissors className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete?.(schedule._id); }}
                  className="p-1 hover:bg-black/5 rounded"
                  title="删除"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Progress bar */}
      {progress > 0 && progress < 100 && (
        <div className="mt-1.5 h-1.5 bg-black/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {cardContent}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs whitespace-pre-line"
        >
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ScheduleTaskCard;
